class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.dots = this.querySelectorAll(`.main-product__media__dots__dot`);
    this.prev = this.querySelector(`.main-product__media__dots__prev`);
    this.next = this.querySelector(`.main-product__media__dots__next`);
    this.count = this.querySelector(
      `.main-product__media__dots__count span:first-child`
    );
    this.container = this.querySelector(`.main-product__media__container`);

    if (!this.dots.length != 0 || !this.prev || !this.next) return;

    // Initialize active dot as the first dot
    this.activeDotIndex = 0;
    this.setActiveDot(this.activeDotIndex);

    this.setupListener();
  }

  /** True when CSS switches the container to horizontal-scroll mode. */
  get isMobileCarousel() {
    if (!this.container) return false;
    return getComputedStyle(this.container).overflowX === 'auto';
  }

  setupListener() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        this.activeDotIndex = index;
        this.setActiveDot(this.activeDotIndex);
        this.updateCount();
        if (this.isMobileCarousel) {
          this.scrollToSlide(index);
        } else {
          const mediaId = this.dots[this.activeDotIndex].dataset.mediaDotId;
          this.updateMedia(mediaId);
        }
      });
    });

    this.prev.addEventListener("click", () => {
      this.activeDotIndex = (this.activeDotIndex - 1 + this.dots.length) % this.dots.length;
      this.setActiveDot(this.activeDotIndex);
      this.updateCount();
      if (this.isMobileCarousel) {
        this.scrollToSlide(this.activeDotIndex);
      } else {
        let activeDot;
        this.dots.forEach(dot => {
          if (dot.classList.contains('__active')) {
            activeDot = dot;
          }
        });
        this.updateMedia(activeDot.dataset.mediaDotId);
      }
    });

    this.next.addEventListener("click", () => {
      this.activeDotIndex = (this.activeDotIndex + 1) % this.dots.length;
      this.setActiveDot(this.activeDotIndex);
      this.updateCount();
      if (this.isMobileCarousel) {
        this.scrollToSlide(this.activeDotIndex);
      } else {
        this.updateMedia(this.dots[this.activeDotIndex].dataset.mediaDotId);
      }
    });

    // On mobile: sync counter + dots when user swipes the carousel.
    if (this.container) {
      let scrollTimeout;
      this.container.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => this.onScrollEnd(), 100);
      }, { passive: true });
    }
  }

  /** After scroll settles, figure out which slide is visible and sync UI. */
  onScrollEnd() {
    if (!this.container || !this.isMobileCarousel) return;
    const slides = this.container.children;
    const center = this.container.scrollLeft + this.container.clientWidth / 2;
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < slides.length; i++) {
      const mid = slides[i].offsetLeft + slides[i].offsetWidth / 2;
      const d = Math.abs(mid - center);
      if (d < minDist) { minDist = d; closest = i; }
    }
    if (closest !== this.activeDotIndex) {
      this.activeDotIndex = closest;
      this.setActiveDot(closest);
      this.updateCount();
    }
  }

  /** Smooth-scroll the carousel to a given slide index. */
  scrollToSlide(index) {
    if (!this.container) return;
    const slide = this.container.children[index];
    if (!slide) return;
    this.container.scrollTo({
      left: slide.offsetLeft - this.container.offsetLeft,
      behavior: 'smooth'
    });
  }

  setActiveMedia(mediaId) {
    const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!activeMedia) return;

    activeMedia.parentElement.prepend(activeMedia);

    // Sync the dots + counter to the variant's media. This previously passed the
    // media-id STRING to setActiveDot, which compares against numeric indices —
    // so NO dot matched: every dot deactivated, the counter froze, and
    // activeDotIndex kept its stale value (the next prev/next click then jumped
    // to the wrong slide). Map the media id to its dot index and sync properly.
    let index = -1;
    this.dots.forEach((dot, i) => {
      if (dot.dataset.mediaDotId === mediaId) index = i;
    });
    if (index >= 0) {
      this.activeDotIndex = index;
      this.setActiveDot(index);
      this.updateCount();
    }
  }

  updateMedia(mediaId) {
    const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!activeMedia) return;

    activeMedia.parentElement.prepend(activeMedia);
  }

  setActiveDot(index) {
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add("__active");
      } else {
        dot.classList.remove("__active");
      }
    });
  }

  updateCount() {
    if (!this.count) return;
    this.count.innerHTML = this.activeDotIndex + 1;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = MediaGallery;
}
